import { useEffect, useMemo, useRef, useState } from 'react';
import type { AutocompleteOption } from '@relay-towing/ui';
import { useGoogleMaps } from './useGoogleMaps';
import { useDebouncedValue } from './useDebouncedValue';
import {
  parseAddressComponents,
  type ParsedAddressComponents,
} from '../lib/companyAddress';

export interface Place {
  address: string;
  lat: number;
  lng: number;
  /** Street, city, state and zip, for a form that stores them apart. */
  parts: ParsedAddressComponents;
}

/** Google is not asked below this, so the list stays shut too. */
const MIN_QUERY_LENGTH = 3;

/**
 * Google Places predictions for the text in an address box, as the library
 * Autocomplete's options, and the chosen one turned into an address with
 * coordinates. The dashboard's PlacesAutocomplete answers the same question;
 * the library's Autocomplete draws the list for both.
 */
export function usePlacePredictions(text: string) {
  const { isLoaded } = useGoogleMaps();
  const query = useDebouncedValue(text.trim());
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [searched, setSearched] = useState(true);
  const chosen = useRef<string | null>(null);

  useEffect(() => {
    if (
      !isLoaded ||
      query.length < MIN_QUERY_LENGTH ||
      query === chosen.current
    ) {
      setPredictions([]);
      return;
    }
    let current = true;
    setSearched(false);
    new google.maps.places.AutocompleteService().getPlacePredictions(
      {
        input: query,
        types: ['address'],
        componentRestrictions: { country: 'us' },
      },
      (results, status) => {
        if (!current) return;
        setPredictions(
          status === google.maps.places.PlacesServiceStatus.OK && results
            ? results
            : []
        );
        setSearched(true);
      }
    );
    return () => {
      current = false;
    };
  }, [isLoaded, query]);

  const options = useMemo<AutocompleteOption[]>(
    () =>
      predictions.map((prediction) => ({
        id: prediction.place_id,
        label: prediction.structured_formatting.main_text,
        description: prediction.structured_formatting.secondary_text,
      })),
    [predictions]
  );

  /** The address, its parts and point of a prediction the person picked. */
  const select = (placeId: string): Promise<Place | null> =>
    new Promise((resolve) => {
      if (!isLoaded) return resolve(null);
      new google.maps.places.PlacesService(
        document.createElement('div')
      ).getDetails(
        {
          placeId,
          fields: ['formatted_address', 'geometry', 'address_components'],
        },
        (place, status) => {
          if (status !== google.maps.places.PlacesServiceStatus.OK || !place)
            return resolve(null);
          const address = place.formatted_address ?? '';
          chosen.current = address.trim();
          resolve({
            address,
            lat: place.geometry?.location?.lat() ?? 0,
            lng: place.geometry?.location?.lng() ?? 0,
            parts: parseAddressComponents(place.address_components ?? []),
          });
        }
      );
    });

  return { options, searched, select };
}
